import React, { useState } from 'react';
import { PageHeader, Section } from '../components/PageShell';
import { Button } from '../components/Button';
import { ArrowUpRight, Clock } from 'lucide-react';

const categories = [
  'All', 'Artificial Intelligence', 'Enterprise Software',
  'Automation', 'Cloud', 'Software Engineering', 'Business', 'Technology'
];

const posts = [
  {
    id: 'ai-in-prod',
    category: 'Artificial Intelligence',
    date: 'Jan 2026',
    read: '9 min read',
    title: 'Getting LLM apps out of the demo phase: the 12 things nobody warned you about.',
    excerpt: 'The gap between a beautiful demo and a production copilot is bigger than most enterprise teams estimate. Here&rsquo;s the checklist we use before signing off on go-live.',
    image: 'https://images.unsplash.com/photo-1677756119517-756a188d2d94?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'legacy-strangle',
    category: 'Enterprise Software',
    date: 'Dec 2025',
    read: '11 min read',
    title: 'Strangling the monolith without strangling the business.',
    excerpt: 'A pragmatic, boring, low-risk playbook for cutting over legacy enterprise systems while shipping new features every single week.',
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'platform-teams',
    category: 'Software Engineering',
    date: 'Nov 2025',
    read: '7 min read',
    title: 'Why your platform team is a product team (whether you like it or not).',
    excerpt: 'Golden paths, service templates and the operating model that lets internal platforms move as fast as customer-facing products.',
    image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'automation-hitl',
    category: 'Automation',
    date: 'Nov 2025',
    read: '6 min read',
    title: 'Enterprise automation actually works when humans are in the loop.',
    excerpt: 'Full autonomy is a demo trick. Reliable enterprise automation is agents + humans + observability &mdash; and knowing exactly where the seams are.',
    image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'security-day-one',
    category: 'Enterprise Software',
    date: 'Oct 2025',
    read: '8 min read',
    title: 'SOC2, HIPAA, ISO 27001 &mdash; from the first commit, not the last.',
    excerpt: 'How we thread compliance into the SDLC so audits become a paperwork exercise, not a re-architecture.',
    image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'cost-engineering',
    category: 'Cloud',
    date: 'Sep 2025',
    read: '6 min read',
    title: 'The cloud bill nobody wants to open: a FinOps starter kit.',
    excerpt: 'Five moves that cut most enterprise cloud bills by 25&ndash;40% without a single migration or vendor change.',
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'business-case-ai',
    category: 'Business',
    date: 'Aug 2025',
    read: '5 min read',
    title: 'Selling AI to a sceptical board: the business case that works.',
    excerpt: 'A structured way to talk about AI ROI &mdash; risk, cost, payback and defensibility &mdash; that CFOs and CTOs actually buy.',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'design-quality',
    category: 'Technology',
    date: 'Aug 2025',
    read: '5 min read',
    title: 'Why enterprise UI still looks bad &mdash; and how we fix it in 90 days.',
    excerpt: 'A working system-first approach that lets B2B products feel as considered as consumer ones, without slowing engineering down.',
    image: 'https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=1200&q=80',
  },
  {
    id: 'devops-2026',
    category: 'Software Engineering',
    date: 'Jul 2025',
    read: '7 min read',
    title: 'What good DevOps looks like in 2026 (and what to stop doing).',
    excerpt: 'Platform engineering, IDPs, policy-as-code &mdash; and the checklists still worth keeping from the 2018 playbook.',
    image: 'https://images.unsplash.com/photo-1517430816045-df4b7de11d1d?auto=format&fit=crop&w=1200&q=80',
  },
];

export default function Insights() {
  const [active, setActive] = useState('All');
  const filtered = active === 'All' ? posts : posts.filter((p) => p.category === active);

  return (
    <div data-testid="insights-page" className="pb-24">
      <PageHeader
        eyebrow="Insights"
        title={<>Field notes from the <span className="font-serif">build</span> floor.</>}
        subtitle="Essays, playbooks and lessons from shipping enterprise software, AI and cloud with our clients."
      />

      <Section>
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`insight-cat-${c.toLowerCase().replace(/\s+/g, '-')}`}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                active === c
                  ? 'bg-[#051A24] text-white'
                  : 'bg-white border border-[#051A24]/10 text-[#0D212C] hover:bg-[#051A24]/5'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <article
              key={p.id}
              data-testid={`insight-card-${p.id}`}
              className="group rounded-[28px] overflow-hidden bg-white border border-[#7C3AED]/12 shadow-[0_4px_16px_rgba(0,0,0,0.04)] hover:shadow-[0_10px_40px_rgba(109,40,217,0.18)] hover:border-[#7C3AED]/35 transition-shadow flex flex-col"
            >
              <div className="aspect-[16/10] overflow-hidden bg-[#F6FCFF]">
                <img
                  src={p.image}
                  alt={p.title.replace(/&mdash;|&ndash;/g, '-')}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-7 flex flex-col flex-1">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#273C46] mb-4">
                  <span>{p.category}</span>
                  <span>·</span>
                  <span>{p.date}</span>
                </div>
                <h3
                  className="font-serif text-xl md:text-2xl text-[#0D212C] leading-tight mb-3"
                  dangerouslySetInnerHTML={{ __html: p.title }}
                />
                <p
                  className="text-sm text-[#273C46] leading-relaxed mb-6"
                  dangerouslySetInnerHTML={{ __html: p.excerpt }}
                />
                <div className="mt-auto flex items-center justify-between text-xs text-[#273C46]">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> {p.read}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[#051A24]">
                    Read <ArrowUpRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </Section>

      <Section>
        <div className="rounded-[40px] bg-gradient-to-br from-[#051A24] via-[#1E1B4B] to-[#4C1D95] text-white p-10 md:p-16 shadow-[0_20px_60px_rgba(76,29,149,0.25)] relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-serif text-3xl md:text-5xl mb-3">Get the next essay first.</h2>
            <p className="text-[#E0EBF0] max-w-lg">One considered write-up per month. No filler, no growth-hacking &mdash; just field notes from the build floor.</p>
          </div>
          <Button to="/contact" variant="secondary" className="!bg-white !text-[#051A24]">Subscribe</Button>
        </div>
      </Section>
    </div>
  );
}
